import type { ProjectDocType } from '@ast-data/shared';
import { asciiFileName } from '../../../utils/downloadFile';

// ---------------------------------------------------------------------------
/**
 * WHICH records an export covers, and what a document of several Maßnahmen is
 * called. Every sheet names its own Maßnahme in the head, so a document may
 * hold sheets from more than one — the cross-project Auswertung produces
 * exactly that, and nothing above the sheet may assume there is only one.
 */
// ---------------------------------------------------------------------------

/**
 * What to print: every record of one Maßnahme, or a named set of records. The
 * project form is the project-level export (a row menu on the Projekte grid);
 * everything else ticks rows and passes their ids, which may span Maßnahmen.
 */
export type ExportScope =
    | { kind: 'project'; projectId: string }
    | { kind: 'ids'; ids: readonly string[] };

export const wholeProject = (projectId: string): ExportScope => ({
    kind: 'project',
    projectId
});

export const theseRecords = (ids: readonly string[]): ExportScope => ({
    kind: 'ids',
    ids
});

/** Nothing to render — an empty tick set is not a request. */
export function isEmptyScope(scope: ExportScope): boolean {
    return scope.kind === 'ids' && scope.ids.length === 0;
}

/**
 * What leads a document's file name: the Maßnahme when the document is of ONE.
 * A selection spanning several is named by how many instead — leading with the
 * first would file it under a Maßnahme most of it is not from, and two such
 * exports would overwrite each other in a folder.
 */
export function measureFileNamePart(measureNumbers: readonly string[]): string {
    const measures = [...new Set(measureNumbers.filter(Boolean))];
    if (measures.length === 1) {
        return measures[0];
    }
    return measures.length > 1 ? `${measures.length}Massnahmen` : '';
}

/**
 * The projects an export may draw on, by id. Read whole rather than one at a
 * time: a cross-project selection would otherwise be one lookup per record.
 */
export function projectsById(
    projects: readonly ProjectDocType[]
): Map<string, ProjectDocType> {
    return new Map(projects.map((project) => [project.id, project]));
}

/**
 * Order records so a document reads as one chapter per Maßnahme. Within one
 * Maßnahme this changes nothing — which is the point: the project-level
 * exports must come out exactly as they did.
 */
export function byMeasureThen<Row>(
    measureOf: (row: Row) => string,
    within: (firstRow: Row, secondRow: Row) => number
): (firstRow: Row, secondRow: Row) => number {
    return (firstRow, secondRow) =>
        asciiFileName(measureOf(firstRow), '').localeCompare(
            asciiFileName(measureOf(secondRow), ''),
            'de',
            { numeric: true }
        ) || within(firstRow, secondRow);
}
